import React, { useEffect } from "react";
import { Alert, Button, Drawer, Form, Input, InputNumber, Space, Tag, Tooltip, Typography } from "antd";
import { LinkOutlined, SaveOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Loading from "components/shared-components/Loading";
import { PICK_NOTE_TEXT_MAX_CHAR } from "constants/InputConstant";
import { isPackageProduct, weightLabel } from "functions/articleCode";
import {
  MAX_PIECES_PER_PACK,
  normalisePiecesByWeight,
  normalisePiecesPerPack,
  orderUnitsForArticle,
  PACK_ADVICE,
  packAdvice,
} from "functions/packaging";
import { normaliseGrams } from "functions/productSync";
import { SHOP_COLORS, shopLabel } from "./constants";

const { Text } = Typography;

/**
 * What the webshop's besteleenheid says about the number typed, under the field: a suggestion
 * while it is empty, a warning when customers can order a quantity that is not whole packages.
 */
const PackAdviceText = ({ product, weight, piecesPerPack }) => {
  const { t } = useTranslation();
  const advice = packAdvice(orderUnitsForArticle(product, weight), piecesPerPack);
  const units = (advice.orderUnits || []).join(" / ");
  if (advice.kind === PACK_ADVICE.SUGGEST) {
    return <Text type="secondary">{t("product_data.pieces_per_pack_suggest", { units })}</Text>;
  }
  if (advice.kind === PACK_ADVICE.LOOSE) {
    return <Text type="warning">{t("product_data.pieces_per_pack_loose", { units, size: advice.size })}</Text>;
  }
  return null;
};

/** The same next to one weight's field: a tag, with the sentence on hover. */
const PackAdviceTag = ({ product, weight, piecesPerPack, sources }) => {
  const { t } = useTranslation();
  const advice = packAdvice(orderUnitsForArticle(product, weight), piecesPerPack);
  const units = (advice.orderUnits || []).join(" / ");
  if (advice.kind === PACK_ADVICE.SUGGEST) {
    return (
      <Tooltip title={t("product_data.pieces_per_pack_suggest", { units })}>
        <Tag>{t("product_data.pieces_per_pack_webshop", { units })}</Tag>
      </Tooltip>
    );
  }
  if (advice.kind === PACK_ADVICE.LOOSE) {
    return (
      <Tooltip title={t("product_data.pieces_per_pack_loose", { units, size: advice.size })}>
        <Tag color="orange">{t("product_data.pieces_per_pack_webshop", { units })}</Tag>
      </Tooltip>
    );
  }
  // A weight only a package asks for: no customer orders it on its own
  if ((sources || []).length > 0 && sources.every((source) => source === "package")) {
    return <Text type="secondary">{t("product_data.pieces_per_pack_package_only")}</Text>;
  }
  return null;
};

/**
 * Editing the warehouse fields of one product.
 *
 * The values are read straight from Firestore when the drawer opens rather than taken from the
 * cached list, and that same snapshot is what the save is checked against - `cms/` is still in
 * use and writes the whole product document, so "what did it say when I opened this" is the
 * only honest basis for a write. Who last touched the product is shown for the same reason:
 * two CMSes on one collection means someone else may be in here too.
 */
const ProductWarehouseDrawer = ({
  open,
  row,
  articles,
  values,
  meta,
  loading,
  saving,
  canEdit,
  counterpartName,
  onSave,
  onEditSyncedProperties,
  onClose,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const piecesPerPack = Form.useWatch("piecesPerPack", form);
  const piecesByWeight = Form.useWatch("piecesPerPackByWeight", form);
  const product = (row && row.product) || null;
  // A package is never picked itself: its components are, each in its own packaging
  const isPackage = isPackageProduct(product);
  // One field per article: per weight for a product with weights (kogelbiefstuk of 125 gram in
  // pairs, of 500 gram not), one for a product without
  const weights = (articles || []).filter((article) => article.weight !== null);
  const weightKey = weights.map((article) => article.weight).join("|");

  useEffect(() => {
    if (!open) return;
    if (!values) {
      form.resetFields();
      return;
    }
    // Stored as a number in some documents and as a numeric string in others; the input needs
    // one type, and a value that is neither (an old typo) must show as empty rather than NaN.
    const byWeight = normalisePiecesByWeight(values.piecesPerPackByWeight);
    // Every weight on screen explicitly, empty included: a nested value is merged into the form,
    // and the previous product's numbers must not stay behind in a field this one also has
    form.resetFields();
    form.setFieldsValue({
      pickWeightGram: normaliseGrams(values.pickWeightGram),
      volumeWeightGram: normaliseGrams(values.volumeWeightGram),
      piecesPerPack: normalisePiecesPerPack(values.piecesPerPack),
      piecesPerPackByWeight: Object.fromEntries(
        weightKey
          .split("|")
          .filter((weight) => !!weight)
          .map((weight) => [weight, byWeight[weight] || null]),
      ),
      pickNote: values.pickNote || "",
    });
  }, [open, values, form, weightKey]);

  const handleSubmit = async () => {
    const submitted = await form.validateFields();
    if (submitted.piecesPerPackByWeight) {
      // A weight that is not on screen any more (its variation was removed) keeps its number: it
      // counts again if the weight comes back, and does nothing until then
      submitted.piecesPerPackByWeight = {
        ...normalisePiecesByWeight(values && values.piecesPerPackByWeight),
        ...submitted.piecesPerPackByWeight,
      };
    }
    onSave(submitted);
  };

  const lastUpdate = () => {
    if (!meta || !meta.updatedAt) return t("product_data.never_updated_here");
    return t("product_data.last_updated", {
      moment: dayjs(meta.updatedAt).format("DD-MM-YYYY HH:mm"),
      user: meta.updatedBy || t("product_data.unknown_user"),
    });
  };

  return (
    <Drawer
      open={open}
      width={520}
      onClose={onClose}
      destroyOnClose
      title={
        !row ? (
          t("product_data.edit_title")
        ) : (
          <Space>
            <Tag color={SHOP_COLORS[row.shopSource] || "default"}>{row.shopSource}</Tag>
            <span>{row.name || row.id}</span>
          </Space>
        )
      }
      footer={
        <Space style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={saving}>
            {t("cancel")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!canEdit || loading}
            onClick={handleSubmit}
          >
            {t("save")}
          </Button>
        </Space>
      }
    >
      {!canEdit && (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message={t("product_data.no_hvo_session_title")}
          description={t("product_data.no_hvo_session_drawer")}
        />
      )}

      {!!counterpartName && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          icon={<LinkOutlined />}
          message={t("product_data.linked_to", {
            shop: shopLabel(row && row.counterpart && row.counterpart.shopSource),
            product: counterpartName,
          })}
          description={t("product_data.linked_to_description")}
          action={
            <Button size="small" icon={<SettingOutlined />} onClick={onEditSyncedProperties}>
              {t("product_data.synced_properties_button")}
            </Button>
          }
        />
      )}

      {loading ? (
        <Loading cover="content" />
      ) : (
        <>
          <Form form={form} layout="vertical" disabled={!canEdit || saving}>
            <Form.Item
              name="pickWeightGram"
              label={t("product_data.pick_weight_gram")}
              extra={t("product_data.pick_weight_gram_help")}
            >
              <InputNumber
                className="w-100"
                min={0}
                step={100}
                precision={0}
                suffix="gram"
                placeholder={t("product_data.optional")}
              />
            </Form.Item>

            <Form.Item
              name="volumeWeightGram"
              label={t("product_data.volume_weight_gram")}
              extra={t("product_data.volume_weight_gram_help")}
            >
              <InputNumber
                className="w-100"
                min={0}
                step={100}
                precision={0}
                suffix="gram"
                placeholder={t("product_data.optional")}
              />
            </Form.Item>

            {isPackage ? (
              <Form.Item label={t("product_data.pieces_per_pack")}>
                <Text type="secondary">{t("product_data.pieces_per_pack_package")}</Text>
              </Form.Item>
            ) : weights.length > 0 ? (
              <Form.Item
                label={t("product_data.pieces_per_pack")}
                extra={
                  <>
                    <div>{t("product_data.pieces_per_pack_help")}</div>
                    <div>{t("product_data.pieces_per_pack_weights_help")}</div>
                  </>
                }
              >
                {weights.map((article) => (
                  <div key={article.articleCode} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ width: 90, flexShrink: 0 }}>{weightLabel(article.weight)}</span>
                    <Form.Item name={["piecesPerPackByWeight", article.weight]} noStyle>
                      <InputNumber
                        style={{ width: 150 }}
                        min={1}
                        max={MAX_PIECES_PER_PACK}
                        step={1}
                        precision={0}
                        suffix={t("product_data.pieces_suffix")}
                        placeholder={t("product_data.pieces_per_pack_placeholder")}
                      />
                    </Form.Item>
                    <PackAdviceTag
                      product={product}
                      weight={article.weight}
                      piecesPerPack={(piecesByWeight || {})[article.weight]}
                      sources={article.sources}
                    />
                  </div>
                ))}
              </Form.Item>
            ) : (
              <Form.Item
                label={t("product_data.pieces_per_pack")}
                extra={
                  <>
                    <div>{t("product_data.pieces_per_pack_help")}</div>
                    <PackAdviceText product={product} weight={null} piecesPerPack={piecesPerPack} />
                  </>
                }
              >
                <Form.Item name="piecesPerPack" noStyle>
                  <InputNumber
                    className="w-100"
                    min={1}
                    max={MAX_PIECES_PER_PACK}
                    step={1}
                    precision={0}
                    suffix={t("product_data.pieces_suffix")}
                    placeholder={t("product_data.pieces_per_pack_placeholder")}
                  />
                </Form.Item>
              </Form.Item>
            )}

            <Form.Item
              name="pickNote"
              label={t("product_data.pick_note")}
              extra={t("product_data.pick_note_help")}
            >
              <Input.TextArea
                rows={2}
                maxLength={PICK_NOTE_TEXT_MAX_CHAR}
                showCount
                placeholder={t("product_data.pick_note_placeholder")}
              />
            </Form.Item>
          </Form>

          <Text type="secondary">{lastUpdate()}</Text>
          <br />
          <Text type="secondary">{t("product_data.other_cms_warning")}</Text>
        </>
      )}
    </Drawer>
  );
};

export default ProductWarehouseDrawer;
